import { Component, OnInit, Input, OnDestroy } from "@angular/core";
import { EdpModalService } from "@edp-components/edp-modal";
import { EdpSmsPaymentsComponent } from "@edp-components/edp-sms-payments/edp-sms-payments.component";
import { ContractService } from "@edp-services/contract.service";
import { PaymentReference, PaymentReferenceData } from "@edp-models/edp-payment-reference.model";
import { Store, select } from "@ngrx/store";
import { Subject } from "rxjs";
import { IAppState } from "@edp-store/state/app.state";
import { takeUntil, filter, take } from "rxjs/operators";
import { getPaymentReference, getContract, getPaymentReferenceError } from "@edp-store/selectors/contract.selectors";
import { GetPaymentReference, SelectContract } from "@edp-store/actions/contract.actions";
import { Contract } from "@edp-components/edp-contract";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { PhoneValidator } from "@edp-validators/phone.validator";
import moment from "moment";
import { EdpMbwayPaymentsComponent } from "@edp-components/edp-mbway-payments/edp-mbway-payments.component";
import { PaymentStatus, PaymentResult, OpenBankingPayment, MbWayPayment } from "@edp-models/edp-payment.model";
import { EdpToasterService, EdpToast } from "@edp-components/edp-toaster/edp-toaster.service";
import { EdpToastType } from "@edp-components/edp-toaster/edp-toast-type";
import { TranslateService } from "@ngx-translate/core";
import { SetRecaptcha, SetBankList, GetBalance } from "@edp-store/actions/dashboard.actions";
import { getBankList } from "@edp-store/selectors/dashboard.selectors";
import { IModalData } from "@edp-components/edp-modal/edp-modal.models";
import { NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { EdpOpenBankingModalComponent } from "@edp-components/edp-open-banking-modal/edp-open-banking-modal.component";
import { PaymentService } from "@edp-services/payment.service";
import { IBankList } from "@edp-models/bank.model";
import { DashboardService } from "@edp-services/dashboard.service";
import { EdpPaymentUserFeedbackModalComponent } from "@edp-components/edp-payment-user-feedback-modal/edp-payment-user-feedback-modal.component";
import { MeddaliaTrackingService } from "@edp-services/meddalia-tracking.service";
import { GetCounter } from "@edp-store/actions/user.actions";
import { getPaymentTab } from "@edp-store/selectors/billing.selectors";
import { SetPaymentTab } from "@edp-store/actions/billing.actions";
import { ShowLoader } from "@edp-store/actions/layout.actions";
import { DatalayerService } from "@edp-services/datalayer.service";
import { Router } from "@angular/router";
import { PaymentMethodEnum } from "@edp-enums/payment-method.enum";
import { sleep } from "@edp-utils/sleep.utils";

const delayPayment = 1500;

@Component({
    selector: "edp-payment-method",
    templateUrl: "./edp-payment-method.component.html",
    styleUrls: ["./edp-payment-method.component.scss"],
})
export class EdpPaymentMethodComponent implements OnInit, OnDestroy {
    @Input() set contractSelected(contract: Contract) {
        this.contract = contract;
    }
    @Input() set balance(balance: { payChannel: any }) {
        this.balanceObject = balance;
    }
    @Input() set isDebt(debt: boolean) {
        this.debt = debt;
        if (!this.debt) {
            this.getPaymentReference();
        }
    }
    @Input() dateLimit: moment.MomentInput;
    @Input() paymentReferenceNoDebt?: PaymentReferenceData;
    public debt: boolean;
    public balanceObject: { debtBalance?: any; balance?: any; payChannel?: any };
    public tabMultibanco: boolean;
    public tabOpenBanking: boolean;
    public tabMbWay: boolean;
    public paymentReference: PaymentReference;
    public contract: Contract;
    public form: FormGroup;
    public showPhoneError: boolean;
    public multibancoLoading = false;
    public mbwayLoading = false;
    public openBankingLoading = false;
    public btnEnabled = false;
    public bankListSelected: IBankList[] = [];
    public canManageBank: boolean;
    public bankSelected: IBankList;
    public isSubmitted: boolean;
    public paymentMethodEnum = PaymentMethodEnum;

    private editMessageDisplayed = false;
    private unsubscribe$: Subject<void> = new Subject();
    private rejectedToaster: EdpToast;
    private successToaster: EdpToast;
    private errorToaster: EdpToast;
    private errorToasterInactiveBank: EdpToast;
    private modalIsOpen = false;
    private isDashboard = false;

    constructor(
        public fb: FormBuilder,
        public edpModalService: EdpModalService,
        public contractService: ContractService,
        public paymentService: PaymentService,
        public store: Store<IAppState>,
        public toaster: EdpToasterService,
        public translate: TranslateService,
        public dashboardService: DashboardService,
        public meddaliaTrackingService: MeddaliaTrackingService,
        public datatalayerService: DatalayerService,
        public router: Router
    ) {
        this.rejectedToaster = this.toaster.create(
            this.translate.instant("pt.edp.edponline.mbwaypayment.toaster.rejected.message"),
            EdpToastType.error
        );

        this.successToaster = this.toaster.create(this.translate.instant("ErrorBlock.Sucesso"), EdpToastType.success);

        this.errorToaster = this.toaster.create(this.translate.instant("pt.edp.edponline.endpoint.error"), EdpToastType.error);

        this.isDashboard = this.router.url.includes("inicio");
    }

    public ngOnInit() {
        this.store.pipe(takeUntil(this.unsubscribe$), select(getPaymentReference)).subscribe(result => {
            this.paymentReference = result;
            this.multibancoLoading = false;
        });

        this.store
            .pipe(
                takeUntil(this.unsubscribe$),
                select(getBankList),
                filter(v => !!v)
            )
            .subscribe((bankList: IBankList[]) => {
                this.bankListSelected = bankList.slice(0);
                if (this.bankListSelected.length === 1) {
                    this.bankSelected = this.bankListSelected[0];
                }
            });

        this.form = this.fb.group({
            phoneNumber: ["", [Validators.required, PhoneValidator(true)]],
        });
        this.setPaymentTab(this.balanceObject);
    }

    public tabClick(type: PaymentMethodEnum) {
        switch (type) {
            case PaymentMethodEnum.MB_WAY:
                this.tabMbWay = true;
                this.tabOpenBanking = false;
                this.tabMultibanco = false;
                break;
            case PaymentMethodEnum.MULTIBANCO:
                this.tabMbWay = false;
                this.tabOpenBanking = false;
                this.tabMultibanco = true;
                if (!this.debt) {
                    this.paymentReference = {
                        paymentData: this.paymentReferenceNoDebt,
                    };
                }
                break;
            case PaymentMethodEnum.OPEN_BANKING:
                this.tabOpenBanking = true;
                this.tabMultibanco = false;
                this.tabMbWay = false;
        }
    }

    private monitoringGetPaymentReferenceError(): void {
        this.store.pipe(select(getPaymentReferenceError)).subscribe(error => {
            console.log("error on getPaymentReferenceError", error);
            if (error) {
                this.multibancoLoading = false;
            }
        });
    }

    public getPaymentReference() {
        this.datatalayerService.setGaClick("clickNavigation", {
            formModal: "multibanco",
            interactionName: "gerar-referencia",
            interactionLocation: !this.isDashboard ? "pagamento-faturas" : "inicio",
        });

        if (!this.debt) {
            this.paymentReference = {
                paymentData: this.paymentReferenceNoDebt,
            };
        } else {
            this.multibancoLoading = true;
            this.store.dispatch(new GetPaymentReference(this.contract));
            this.monitoringGetPaymentReferenceError();
        }
    }

    public openGetRefMbModal() {
        this.datatalayerService.setGaClick("paymentMBSendSMS", {
            interactionLocation: !this.isDashboard ? "pagamento-faturas" : "inicio",
        });

        const data = {
            contractId: this.contract.ContractId,
            faedn: moment(this.dateLimit).format("DD/MM/YYYY"),
            collection: {
                gaEvent: "paymentMBSendSMSOpen",
            },
        };
        this.openModal(EdpSmsPaymentsComponent, data, true, true);
    }

    private datalayerMBWaySubmitError(): void {
        this.datatalayerService.setGaClick("paymentMBwayError", {
            interactionLocation: !this.isDashboard ? "mbway" : "inicio",
            setAttributes: { campoComErro: "Numero de Telemovel Associado" },
        });
    }

    private datalayerMBWaySubmit(): void {
        this.datatalayerService.setGaClick("clickOpen", {
            formModal: "pagamento-mbway",
            interactionName: !this.isDashboard ? "confirmar" : "pagar",
            interactionLocation: !!this.isDashboard ? "inicio" : "mbway",
        });
    }

    private async getBalanceAfterPayment(status: PaymentStatus): Promise<void> {
        if (status === PaymentStatus.PAYMENT_STATUS_SUCCESS) {
            await sleep(delayPayment);
            this.store.dispatch(new GetBalance(this.contract));
        }
    }

    public mbwaySubmit(): void {
        if (this.form.get("phoneNumber").errors && this.form.get("phoneNumber").errors.invalidPhoneNumber) {
            this.showPhoneError = true;
            this.datalayerMBWaySubmitError();
            return;
        }

        // force error message to be hidden
        this.showPhoneError = false;
        this.isSubmitted = true;

        if (!this.form.valid) {
            this.isSubmitted = false;
            this.form.setValue({ phoneNumber: "" });
            return;
        }

        this.datalayerMBWaySubmit();

        this.mbwayLoading = true;
        const amountAux: number = this.debt ? this.balanceObject.debtBalance : this.balanceObject.balance;
        this.paymentService
            .triggerMbwayPayment(this.form.value.phoneNumber.toString(), this.contract, amountAux, this.debt)
            .subscribe(
                this.waitForPayment(async (payment) => {
                    await this.getBalanceAfterPayment(payment.status);
                    this.mbwayLoading = false;
                    this.isSubmitted = false;
                })
            );
    }

    private datalayerOpenBanking(): void {
        this.datatalayerService.setGaClick("clickOpenLink", {
            formModal: this.bankSelected.institution_name.toLocaleLowerCase().replace(" ", "-"),
            formType: "faturacao",
            interactionName: !!this.isDashboard ? "confirmar" : "pagar",
            interactionLocation: !this.isDashboard ? "pagamento-faturas" : "inicio",
        });
    }

    public openBankingSubmit(): void {
        this.openBankingLoading = true;

        if (this.bankSelected.bank_status === "0") {
            this.openBankingLoading = false;
            this.showToaster();
            return;
        }

        this.datalayerOpenBanking();

        const amountAux: number = this.debt ? this.balanceObject.debtBalance : this.balanceObject.balance;
        this.paymentService
            .triggerOpenbankingPayment(
                this.bankSelected.bank_identifier,
                this.bankSelected.institution_name,
                this.contract,
                amountAux,
                this.debt
            )
            .subscribe(
                this.waitForPayment(async payment => {
                    await this.getBalanceAfterPayment(payment.status);
                    this.openBankingLoading = false;
                })
            );
    }

    private showToaster() {
        this.errorToasterInactiveBank = this.toaster.create(
            this.translate.instant("pt.edp.edponline.inactive.bank.selected.error", { bankName: this.bankSelected.institution_name }),
            EdpToastType.error
        );
        this.errorToasterInactiveBank.show();
    }

    public addBank() {
        this.bankSelected = null;

        this.datatalayerService.setGaClick("paymentOBAddBank", {
            interactionLocation: !!this.isDashboard ? "inicio" : "pagamento-faturas",
        });

        const modalOptions: NgbModalOptions = {
            windowClass: "edp-dialog-fullscreen",
        };

        const modalData = {
            title: "",
            showClose: true,
            editMessageDisplayed: this.editMessageDisplayed,
            collection: {
                gaEvent: "paymentOBAddBankOpen",
            },
        } as IModalData;

        this.edpModalService.custom(EdpOpenBankingModalComponent, modalOptions, modalData).then(() => {
            /* intentional event resolve */
        });

        this.editMessageDisplayed = true;
    }

    public deleteBank(index: number) {
        const bank: IBankList = this.bankListSelected[index];
        this.dashboardService.deleteBank(bank.id, this.contract.ContractId).subscribe(() => {
            /* intentional event resolve */
        });
        this.bankListSelected.splice(index, 1);
        this.store.dispatch(new SetBankList(this.bankListSelected.slice(0)));
    }

    public manageBank(can: boolean) {
        this.canManageBank = can;

        !!can
            ? this.datatalayerService.setGaClick("paymentOBManageBanks", {
                  interactionLocation: !this.isDashboard ? "pagamento-faturas" : "inicio",
              })
            : this.datatalayerService.setGaClick("clickClose", { interactionLocation: "fechar-gestao-bancos" });
    }

    public bankSelection(bank) {
        this.bankSelected = bank;
    }

    public ngOnDestroy() {
        this.unsubscribe$.next();
        this.store.dispatch(new SetRecaptcha(false));
        this.unsubscribe$.complete();
        this.store.dispatch(new SetPaymentTab(null));
    }

    private openModal(component: any, data: any, canClose: boolean, insideDialog = false): Promise<any> {
        const { options } = this.contractService.getFullModalParam(canClose, insideDialog);

        return this.edpModalService.custom(component, options, data).then(modalOpened => {
            this.modalIsOpen = modalOpened;
        });
    }

    private datalayerPaymentError(): void {
        this.datatalayerService.setGaEvent("paymentMBwaySubmit", {
            serviceAttributes: {
                mensagemResultado: this.translate.instant("pt.edp.edponline.endpoint.error"),
            },
        });
    }

    private datalayerPaymentSuccess(): void {
        this.datatalayerService.setGaEvent("paymentMBwaySubmit", {
            serviceAttributes: {
                mensagemResultado: this.translate.instant("ErrorBlock.Sucesso"),
            },
        });
    }

    private waitForPayment(cleanUpFunction?: (p: PaymentResult) => void) {
        return (payment: PaymentResult) => {
            switch (payment.status) {
                case PaymentStatus.RECAPTCHA_FAILED:
                case PaymentStatus.PAYMENT_FAILED:
                    if (!this.modalIsOpen) {
                        if (payment instanceof OpenBankingPayment) {
                            this.openBankingLoading = false;
                        } else if (payment instanceof MbWayPayment) {
                            this.mbwayLoading = false;
                            this.isSubmitted = false;

                            this.datalayerPaymentError();
                        }
                        // error creating payment on the server
                        if (!this.errorToaster.getVisibility()) {
                            this.errorToaster.show();
                        }
                    }
                    break;
                case PaymentStatus.PAYMENT_CREATED:
                    // case PaymentStatus.PAYMENT_STATUS_PENDING:
                    // display modal
                    if (!this.modalIsOpen) {
                        this.openModal(EdpMbwayPaymentsComponent, PaymentService.getPaymentObservable(payment), true).finally(
                            cleanUpFunction.bind(this, payment)
                        );
                        this.modalIsOpen = true;
                        if (payment instanceof OpenBankingPayment) {
                            window.open(payment.redirectUrl, "_blank");
                        }
                    }
                    break;
                case PaymentStatus.PAYMENT_STATUS_CANCELLED:
                case PaymentStatus.PAYMENT_STATUS_EXPIRED:
                case PaymentStatus.PAYMENT_STATUS_REJECTED:
                    if (!this.modalIsOpen) {
                        if (payment instanceof OpenBankingPayment) {
                            this.openBankingLoading = false;
                            this.openModal(EdpPaymentUserFeedbackModalComponent, payment, true).finally(
                                cleanUpFunction.bind(this, payment)
                            );
                        } else if (payment instanceof MbWayPayment) {
                            if (!this.rejectedToaster.getVisibility()) {
                                this.rejectedToaster.show();
                            }

                            this.datalayerPaymentError();
                        }
                    }
                    break;
                case PaymentStatus.PAYMENT_STATUS_SUCCESS:
                    if (!this.modalIsOpen) {
                        if (!this.successToaster.getVisibility()) {
                            this.successToaster.show();

                            if (!!this.tabMbWay) {
                                this.datalayerPaymentSuccess();
                            }
                        }
                    }
                    if (!!this.tabMbWay || !!this.tabOpenBanking) {
                        this.meddaliaTrackingService.setMedalliaTracking(!!this.tabMbWay ? "Pagamento MBWay" : "Pagamento Openbanking");
                    }

                    this.store.pipe(select(getContract), take(1)).subscribe(contract => {
                        this.store.dispatch(new SelectContract(contract));
                    });
                    this.store.dispatch(new GetCounter());
                    break;
            }
        };
    }

    private setDefaultTab(): void {
        const defaultTab = !this.contract?.HasServiceFaturaSegura ? PaymentMethodEnum.MB_WAY : PaymentMethodEnum.MULTIBANCO;
        this.tabClick(defaultTab);
    }

    private setPaymentTab(balance) {
        this.store.pipe(select(getPaymentTab)).subscribe(tab => {
            if (!!tab) {
                this.store.dispatch(new ShowLoader(false));
                this.tabClick(tab);
                return;
            }

            if (!!this.contract?.HasServiceFaturaSegura) {
                return this.setDefaultTab();
            }

            switch (balance?.payChannel) {
                case "MBWAY":
                    this.tabMbWay = true;
                    break;
                case "PSD2":
                    this.tabOpenBanking = true;
                    break;
                default:
                    this.setDefaultTab();
                    break;
            }
        });
    }
}
